import { supabase } from '@/integrations/supabase/client';
import { logger as baseLogger } from '@/lib/structured-logger';
import { ServiceError } from '@/lib/service-error';

const logger = baseLogger.child({ service: 'media' });

export interface MediaAsset {
  id: string;
  organization_id: string;
  user_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  width: number | null;
  height: number | null;
  alt_text: string | null;
  tags: string[] | null;
  created_at: string;
  updated_at: string;
}

class MediaService {
  async getAssets(organizationId: string): Promise<MediaAsset[]> {
    try {
      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as MediaAsset[];
    } catch (error) {
      logger.error('Failed to fetch media assets', { error, organizationId });
      throw new ServiceError('Failed to fetch media assets', 'DATABASE_ERROR');
    }
  }

  async uploadAsset(
    file: File,
    organizationId: string,
    userId: string,
    tags?: string[],
    altText?: string
  ): Promise<MediaAsset> {
    try {
      // Validate inputs
      if (!organizationId || !userId) {
        throw new Error('Organization ID and User ID are required');
      }

      // Upload to Supabase Storage with sanitized filename
      const sanitizedName = this.sanitizeFileName(file.name);
      const filePath = `${organizationId}/${Date.now()}-${sanitizedName}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('media-library')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        // Extract actual error message
        const errorMessage = uploadError.message || 
          (typeof uploadError === 'object' ? JSON.stringify(uploadError) : String(uploadError));
        
        // Extract statusCode safely from the error object
        const statusCode = 'statusCode' in uploadError ? (uploadError as { statusCode?: number }).statusCode : undefined;
        
        logger.error('Storage upload failed', { 
          error: errorMessage,
          statusCode,
          bucket: 'media-library',
          filePath,
          fileSize: file.size,
          mimeType: file.type
        });
        
        // Provide user-friendly error messages
        if (errorMessage.includes('row-level security') || errorMessage.includes('policy')) {
          throw new Error('Permission denied. Please ensure you are logged in.');
        }
        if (errorMessage.includes('size') || file.size > 10 * 1024 * 1024) {
          throw new Error('File too large. Maximum size is 10MB.');
        }
        if (errorMessage.includes('mime') || errorMessage.includes('type')) {
          throw new Error('File type not supported. Please upload an image file.');
        }
        
        throw new Error(`Upload failed: ${errorMessage}`);
      }

      // Get dimensions if image
      let width: number | null = null;
      let height: number | null = null;

      if (file.type.startsWith('image/')) {
        const dimensions = await this.getImageDimensions(file);
        width = dimensions.width;
        height = dimensions.height;
      }

      // Create database record
      const { data, error } = await supabase
        .from('media_assets')
        .insert({
          organization_id: organizationId,
          user_id: userId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type,
          width,
          height,
          alt_text: altText || null,
          tags: tags || null,
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('No data returned from insert');
      logger.info('Media asset uploaded', { assetId: data.id, organizationId });
      return data as MediaAsset;
    } catch (error) {
      logger.error('Failed to upload media asset', { error, organizationId });
      // Re-throw the original error to preserve specific error messages
      if (error instanceof Error) {
        throw error;
      }
      throw new ServiceError('Failed to upload media', 'NETWORK_ERROR');
    }
  }

  async deleteAsset(assetId: string): Promise<void> {
    try {
      // Get asset details
      const { data: asset, error: fetchError } = await supabase
        .from('media_assets')
        .select('file_path')
        .eq('id', assetId)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!asset) throw new ServiceError('Media asset not found', 'NOT_FOUND');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('media-library')
        .remove([asset.file_path]);

      if (storageError) throw storageError;

      // Delete database record
      const { error: deleteError } = await supabase
        .from('media_assets')
        .delete()
        .eq('id', assetId);

      if (deleteError) throw deleteError;
      logger.info('Media asset deleted', { assetId });
    } catch (error) {
      logger.error('Failed to delete media asset', { error, assetId });
      throw new ServiceError('Failed to delete media', 'DATABASE_ERROR');
    }
  }

  async updateAsset(
    assetId: string,
    updates: { alt_text?: string; tags?: string[] }
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('media_assets')
        .update(updates)
        .eq('id', assetId);

      if (error) throw error;
      logger.info('Media asset updated', { assetId });
    } catch (error) {
      logger.error('Failed to update media asset', { error, assetId });
      throw new ServiceError('Failed to update media', 'DATABASE_ERROR');
    }
  }

  /**
   * Get a signed URL for a media asset (1 hour expiry)
   * Required since buckets are private for security
   */
  async getSignedUrl(filePath: string, expiresInSeconds: number = 3600): Promise<string> {
    const { data, error } = await supabase.storage
      .from('media-library')
      .createSignedUrl(filePath, expiresInSeconds);
    
    if (error || !data?.signedUrl) {
      logger.error('Failed to create signed URL', { error, filePath });
      throw new ServiceError('Failed to get media URL', 'INTERNAL_ERROR');
    }
    return data.signedUrl;
  }

  /**
   * Get signed URLs for multiple assets at once (batch operation)
   */
  async getSignedUrls(filePaths: string[], expiresInSeconds: number = 3600): Promise<Map<string, string>> {
    const { data, error } = await supabase.storage
      .from('media-library')
      .createSignedUrls(filePaths, expiresInSeconds);
    
    if (error) {
      logger.error('Failed to create signed URLs', { error });
      throw new ServiceError('Failed to get media URLs', 'INTERNAL_ERROR');
    }

    const urlMap = new Map<string, string>();
    data?.forEach(item => {
      if (item.signedUrl) {
        urlMap.set(item.path, item.signedUrl);
      }
    });
    return urlMap;
  }

  /**
   * @deprecated Use getSignedUrl instead - buckets are now private
   */
  async getPublicUrl(filePath: string): Promise<string> {
    // For backwards compatibility, return signed URL instead
    return this.getSignedUrl(filePath);
  }

  async analyzeImage(imageUrl: string): Promise<{
    tags: string[];
    description: string;
    altText: string;
  }> {
    try {
      const { data, error } = await supabase.functions.invoke('analyze-image', {
        body: { imageUrl }
      });

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Failed to analyze image', { error, imageUrl });
      throw new ServiceError('Failed to analyze image with AI', 'NETWORK_ERROR');
    }
  }

  private sanitizeFileName(fileName: string): string {
    // Replace spaces and special characters with underscores
    // Keep only alphanumeric, dots, dashes, and underscores
    return fileName
      .replace(/\s+/g, '_')           // Replace spaces with underscores
      .replace(/[^a-zA-Z0-9._-]/g, '') // Remove other special chars
      .toLowerCase();
  }

  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }
}

export const mediaService = new MediaService();
